import React, { useEffect, useMemo, useState } from 'react';
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  TextField,
  FormControl,
  MenuItem,
} from '@material-ui/core';
import { range } from 'lodash';

// Utils.
import { monthNames } from '../../../../helpers/utils';

// Components.
import { CurrencyFormatter } from '../../shared/Formatters';

const now = new Date();
const currentYear = now.getFullYear();
const currentMonth = now.getMonth() + 1;
const yearOptions = range(1949, currentYear + 1).reverse();

const monthOptions = monthNames.slice(1).map((month, index) => ({
  id: index + 1,
  name: month
}));

const addAumAggregationInitialState = {
  year: yearOptions[0],
  month: null,
  total_aum: null,
  minimum_account_size: null,
  source: null,
};

const AddAumAggregationDialog = ({
  open,
  onClose,
  aumAggregations,
  handleAddAumAggregation
}) => {
  const [formData, setFormData] = useState(addAumAggregationInitialState);

  useEffect(() => {
    if (open) {
      setFormData(addAumAggregationInitialState);
    }
  }, [open]);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleAdd = () => {
    if (isDuplicate) {
      return;
    }
    handleAddAumAggregation(formData);
    onClose();
  };

  const isDuplicate = useMemo(() => {
    return (aumAggregations ?? []).some((a) => {
      const sameYear = Number(a.year) === Number(formData.year);
      const bothWithoutMonth =
        (a.month == null || a.month === '') &&
        (formData.month == null || formData.month === '');
      const sameMonth = Number(a.month) === Number(formData.month);
      return sameYear && (bothWithoutMonth || sameMonth);
    });
  }, [aumAggregations, formData.year, formData.month]);

  const isEmpty = useMemo(() => {
    return (aumAggregations ?? []).some((a) => {
      const withoutMonth =
        (a.month == null || a.month === '') &&
        (formData.month == null || formData.month === '');
      return withoutMonth;
    });
  }, [ formData.month]);

  const isFutureMonth = useMemo(() => {
    if (!formData.year || !formData.month) {
      return false;
    }
    if (formData.year > currentYear) {
      return true;
    }
    return formData.year === currentYear && formData.month > currentMonth;
  }, [formData.year, formData.month]);

  const filteredMonthOptions = useMemo(() => {
    if (!formData.year) {
      return monthOptions;
    }
    if (formData.year < currentYear) {
      return monthOptions;
    }
    if (formData.year > currentYear) {
      return [];
    }
    return monthOptions.filter(m => m.id <= currentMonth);
  }, [formData.year]);

  const parseNumberOrNull = (value) => value === '' ? null : Number(value);

  return (
    <Dialog open={open} onClose={onClose} maxWidth='xs' fullWidth>
      <DialogTitle>Add Aum Aggregation</DialogTitle>

      <DialogContent dividers>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <FormControl fullWidth>
              <TextField
                select
                label='Year'
                variant='outlined'
                required
                value={formData.year}
                onChange={(e) => handleChange('year', Number(e.target.value))}
              >
                {yearOptions.map((year) => (
                  <MenuItem key={year} value={year}>
                    {year}
                  </MenuItem>
                ))}
              </TextField>
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            <FormControl fullWidth>
              <TextField
                select
                label='Month'
                variant='outlined'
                value={formData.month ?? ''}
                error={isDuplicate || isFutureMonth || isEmpty}
                helperText={
                  isEmpty ? 'Month cannot be empty' :
                    isFutureMonth
                        ? 'Cannot select a future month'
                        : isDuplicate
                            ? 'Aggregation already exists for this year and month'
                            : ''
                }
                onChange={(e) => handleChange('month', parseNumberOrNull(e.target.value))}
              >
                <MenuItem value=''>
                  <em>None</em>
                </MenuItem>
                {filteredMonthOptions.map((month) => (
                  <MenuItem key={month.id} value={month.id}>
                    {month.name}
                  </MenuItem>
                ))}
              </TextField>
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            <FormControl fullWidth>
              <TextField
                required
                label='AUM'
                variant='outlined'
                value={formData.total_aum ?? ''}
                error={formData.total_aum != null && formData.total_aum <= 0}
                helperText={
                  formData.total_aum != null && formData.total_aum <= 0
                    ? 'AUM must be greater than 0'
                    : ''
                }
                onChange={(e) => handleChange('total_aum', parseNumberOrNull(e.target.value))}
                InputLabelProps={{ shrink: formData.total_aum != null }}
                InputProps={{ inputComponent: CurrencyFormatter }}
              />
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            <FormControl fullWidth>
              <TextField
                label='Minimum Account Size'
                variant='outlined'
                value={formData.minimum_account_size ?? ''}
                onChange={(e) => handleChange('minimum_account_size', parseNumberOrNull(e.target.value))}
                InputLabelProps={{ shrink: formData.minimum_account_size != null }}
                InputProps={{ inputComponent: CurrencyFormatter }}
              />
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            <FormControl fullWidth>
              <TextField
                label='Source'
                variant='outlined'
                value={formData.source ?? ''}
                onChange={(e) => handleChange('source', e.target.value)}
              />
            </FormControl>
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          onClick={handleAdd}
          color='primary'
          variant='contained'
          disabled={
            !formData.year ||
            formData.total_aum == null ||
            formData.total_aum <= 0 ||
            isDuplicate ||
            isFutureMonth ||
            isEmpty
          }
        >
          Add
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddAumAggregationDialog;
